import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSupportChat } from "@/hooks/useSupportChat";

/**
 * A conversa com o Lucas, uma só, para a plataforma inteira.
 *
 * Antes isto guardava apenas `isOpen`, porque o Lucas morava só na home. Com a
 * bolinha flutuante existem dois lugares que mostram a MESMA conversa, e foi
 * exatamente isso que quebrou na primeira tentativa: o painel flutuante antigo
 * chamava `useSupportChat` por conta própria enquanto a home chamava outro, e
 * os dois arrays de `messages` divergiam sobre uma única conversa do servidor.
 * Agora o hook é instanciado aqui, uma vez, e os dois consumidores leem o mesmo
 * estado.
 *
 * ## Por que dois contextos
 *
 * O provider embrulha a plataforma inteira. Se o estado da conversa e o estado
 * de aberto/fechado viajassem juntos, cada token que o Lucas escreve
 * re-renderizaria todo componente que só queria saber se o painel está aberto
 * (o botão da sidebar, por exemplo). Separar deixa cada consumidor assinar só
 * o que ele usa.
 *
 * O `children` continua barato de qualquer forma: ele chega como prop, então é
 * o MESMO elemento entre renders e o React não desce nele quando só o estado
 * daqui muda. Quem re-renderiza é quem chama `useContext`.
 *
 * ## Por que começa dormente
 *
 * `isActive` só vira `true` no primeiro gesto de interesse (abrir o painel, ou
 * focar o campo de perguntas da home). Até lá o `useSupportChat` não busca a
 * conversa nem assina o socket. Sem isso, montar o provider em toda a
 * plataforma custaria uma requisição e uma sala de socket por pessoa logada,
 * sendo que cerca de 10% da base usa o Lucas num mês.
 */

const SupportChatUIContext = createContext(null);
const SupportChatSessionContext = createContext(null);

export function SupportChatProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const chat = useSupportChat({ enabled: isActive });

  /**
   * Ligar sem abrir.
   *
   * O campo da home envia a pergunta ANTES de abrir a conversa, e a resposta
   * volta por socket. Se a assinatura só acontecesse no `open()`, o turno
   * poderia ser respondido antes de existir alguém ouvindo. Focar o campo já
   * liga, o que acontece segundos antes de qualquer envio.
   */
  const activate = useCallback(() => setIsActive(true), []);

  const open = useCallback(() => {
    setIsActive(true);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => {
    setIsActive(true);
    setIsOpen((v) => !v);
  }, []);

  /**
   * Quantas respostas chegaram com o painel fechado.
   *
   * É o único aviso que o atalho dá por iniciativa do SERVIDOR, e por isso o
   * único que vale sempre: alguém do time respondeu e a pessoa não viu. A
   * pulsada de descoberta é outra coisa, tem teto e acaba; esta não, porque
   * não é chamariz, é recado.
   *
   * Vive no contexto de UI, e não no da conversa, para o atalho poder
   * mostrá-lo sem assinar o estado que muda a cada token.
   */
  const [unread, setUnread] = useState(0);
  /**
   * O corte é por HORÁRIO, não por posição na lista.
   *
   * Contar "o que entrou depois do último índice visto" parece equivalente e
   * não é: ao abrir a plataforma, a conversa carrega o histórico de uma vez, e
   * toda resposta antiga entraria na lista de um golpe só. A pessoa abriria o
   * dia com um "7" vermelho sobre mensagens que ela já leu ontem.
   *
   * Só conta o que nasceu depois de esta aba abrir, que é exatamente o que ela
   * ainda não teve chance de ver.
   */
  const abertoEmRef = useRef(Date.now());
  const contadasRef = useRef(new Set());
  const mensagens = chat.messages;

  useEffect(() => {
    if (isOpen) {
      // Abrir é ler: o que estava pendente deixa de estar, e o que chegar com
      // o painel na tela não vira recado.
      mensagens.forEach((m) => contadasRef.current.add(m.id));
      setUnread(0);
      return;
    }

    const novas = mensagens.filter(
      (m) =>
        (m.role === "assistant" || m.role === "agent") &&
        !contadasRef.current.has(m.id) &&
        new Date(m.createdAt).getTime() >= abertoEmRef.current,
    );
    if (novas.length === 0) return;

    novas.forEach((m) => contadasRef.current.add(m.id));
    setUnread((atual) => atual + novas.length);
  }, [mensagens, isOpen]);

  const ui = useMemo(
    () => ({ isOpen, isActive, unread, open, close, toggle, activate }),
    [isOpen, isActive, unread, open, close, toggle, activate],
  );

  return (
    <SupportChatUIContext.Provider value={ui}>
      <SupportChatSessionContext.Provider value={chat}>
        {children}
      </SupportChatSessionContext.Provider>
    </SupportChatUIContext.Provider>
  );
}

/** Aberto/fechado e o gesto que liga a conversa. Muda pouco. */
export function useSupportChatUI() {
  const context = useContext(SupportChatUIContext);
  if (!context) {
    throw new Error(
      "useSupportChatUI precisa de <SupportChatProvider> acima na árvore",
    );
  }
  return context;
}

/**
 * A conversa em si: mensagens, envio, anexos, handover.
 *
 * Muda a cada token, então só chame em quem realmente desenha a conversa.
 */
export function useSupportChatSession() {
  const context = useContext(SupportChatSessionContext);
  if (!context) {
    throw new Error(
      "useSupportChatSession precisa de <SupportChatProvider> acima na árvore",
    );
  }
  return context;
}

export default SupportChatUIContext;
